import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  type AssignmentScope,
  MILESTONE_DAYS,
  type MilestoneDay,
  type OnboardingResource,
} from "@shared/schema";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { AddResourceModal } from "./add-resource-modal";
import { AssignmentRow } from "./assignment-row";
import type { MilestonesDataSource } from "./data-source";
import { EditResourceDialog } from "./edit-resource-dialog";
import { labelForRenderType } from "./render-type-labels";

// Renders the welcome header + 7 milestone columns + picker + scope modal
// + drag overlay. Backed entirely by a `MilestonesDataSource`, so the same
// panel works for template editing and per-hire customization.

type Props = {
  dataSource: MilestonesDataSource;
  /** Owner check is delegated to the data source; this controls UI affordances. */
  isOwner: boolean;
  /** Optional editor rendered inside each milestone section (above its list). */
  renderWelcomeForDay?: (day: MilestoneDay) => React.ReactNode;
  /** Called when a resource title is clicked. Consumers render the resource
   *  preview inline (template canvas + unified onboarding dashboard). */
  onResourceClick?: (resource: OnboardingResource) => void;
  /** When true, hides the "Boas-vindas, {primeiro_nome}!" header on top of
   *  the panel. The onboarding dashboard sets this because the management
   *  header already shows the hire's name. */
  hideWelcomeHeader?: boolean;
};

export function CanvasMilestonesPanel({
  dataSource,
  isOwner,
  renderWelcomeForDay,
  onResourceClick,
  hideWelcomeHeader = false,
}: Props) {
  const { toast } = useToast();
  const goToResourcePreview = (resource: OnboardingResource) => {
    onResourceClick?.(resource);
  };
  const [pickerDay, setPickerDay] = useState<MilestoneDay | null>(null);
  const [editingResourceId, setEditingResourceId] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [overDay, setOverDay] = useState<MilestoneDay | null>(null);
  const [collapsedDays, setCollapsedDays] = useState<Set<MilestoneDay>>(() => new Set());

  function toggleDay(day: MilestoneDay) {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const assignmentsByDay = new Map<MilestoneDay, typeof dataSource.assignments>();
  for (const day of MILESTONE_DAYS) {
    assignmentsByDay.set(day, []);
  }
  for (const a of dataSource.assignments) {
    const day = a.milestoneDay as MilestoneDay;
    const list = assignmentsByDay.get(day);
    if (list) list.push(a);
  }

  const dayByAssignmentId = new Map<string, MilestoneDay>();
  for (const day of MILESTONE_DAYS) {
    const list = assignmentsByDay.get(day) ?? [];
    for (const a of list) dayByAssignmentId.set(a.id, day);
  }

  function handleApiError(err: unknown, fallback?: string) {
    const code: string | undefined = (err as { data?: { code?: string } } | null)?.data?.code;
    const message: string | undefined = (err as { message?: string } | null)?.message;
    if (code === "duplicate_in_milestone") {
      toast({ title: "Já adicionado a este dia", variant: "destructive" });
      return;
    }
    if (code === "not_owner") {
      toast({ title: "Apenas o dono do template pode editar", variant: "destructive" });
      return;
    }
    toast({
      title: fallback ?? "Operação falhou",
      description: message,
      variant: "destructive",
    });
  }

  function handleAddResourceToDay(day: MilestoneDay) {
    dataSource.setPickerEnabled(true);
    setPickerDay(day);
  }

  async function handlePickResource(resource: OnboardingResource) {
    if (pickerDay === null) return;
    const scope: AssignmentScope = resource.scope === "company" ? "empresa" : "disciplina";
    try {
      await dataSource.addAssignment({
        resourceId: resource.id,
        milestoneDay: pickerDay,
        scope,
      });
      await dataSource.invalidate();
    } catch (err) {
      handleApiError(err, "Não foi possível adicionar o recurso");
    }
  }

  const undoActionRef = useRef<(() => Promise<void>) | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearUndo = useCallback(() => {
    undoActionRef.current = null;
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearUndo(), [clearUndo]);

  async function handleRemove(assignmentId: string) {
    const removed = dataSource.assignments.find((a) => a.id === assignmentId);
    if (!removed) return;
    const snapshot = {
      resourceId: removed.resourceId,
      milestoneDay: removed.milestoneDay as MilestoneDay,
      scope: removed.scope,
      position: removed.position,
      title: removed.resource.title,
    };
    try {
      await dataSource.removeAssignment({ assignmentId });
      await dataSource.invalidate();
    } catch (err) {
      handleApiError(err, "Não foi possível remover");
      return;
    }

    const runUndo = async () => {
      clearUndo();
      try {
        const created = await dataSource.addAssignment({
          resourceId: snapshot.resourceId,
          milestoneDay: snapshot.milestoneDay,
          scope: snapshot.scope,
        });
        if (created) {
          await dataSource.moveAssignment({
            assignmentId: created.id,
            milestoneDay: snapshot.milestoneDay,
            position: snapshot.position,
          });
        }
        await dataSource.invalidate();
      } catch (err) {
        handleApiError(err, "Não foi possível desfazer");
      }
    };

    undoActionRef.current = runUndo;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      undoActionRef.current = null;
      undoTimerRef.current = null;
    }, 10000);

    const t = toast({
      title: "Recurso removido",
      description: snapshot.title,
      action: (
        <ToastAction
          altText="Desfazer remoção"
          onClick={() => {
            t.dismiss();
            void runUndo();
          }}
        >
          Desfazer
        </ToastAction>
      ),
    });
  }

  useEffect(() => {
    if (!isOwner) return;
    function onKeyDown(event: KeyboardEvent) {
      const isUndo =
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === "z";
      if (!isUndo) return;
      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable) {
          return;
        }
      }
      const action = undoActionRef.current;
      if (!action) return;
      event.preventDefault();
      void action();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOwner]);

  function parseDayDroppableId(id: string): MilestoneDay | null {
    if (!id.startsWith("day-")) return null;
    const n = Number(id.slice(4));
    return (MILESTONE_DAYS as readonly number[]).includes(n) ? (n as MilestoneDay) : null;
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(String(event.active.id));
  }

  function handleDragOver(event: DragOverEvent) {
    const { over } = event;
    if (!over) {
      setOverDay(null);
      return;
    }
    const overId = String(over.id);
    const day = parseDayDroppableId(overId) ?? dayByAssignmentId.get(overId) ?? null;
    setOverDay(day);
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    setOverDay(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const fromDay = dayByAssignmentId.get(activeId);
    if (fromDay === undefined) return;

    const dayDrop = parseDayDroppableId(overId);
    const toDay = dayDrop ?? dayByAssignmentId.get(overId);
    if (toDay === undefined) return;

    const targetList = assignmentsByDay.get(toDay) ?? [];
    let targetPosition: number;
    if (dayDrop !== null) {
      // Dropped on the day container itself, not on a row. Only honor this
      // when the target list is empty (without the source) — otherwise it's
      // a near-miss between rows, and snapping to the end surprises the user.
      const targetWithoutSource = targetList.filter((a) => a.id !== activeId);
      if (targetWithoutSource.length > 0) return;
      targetPosition = 0;
    } else {
      const overIndex = targetList.findIndex((a) => a.id === overId);
      if (overIndex < 0) return;
      if (fromDay === toDay) {
        targetPosition = overIndex;
      } else {
        targetPosition = Math.min(overIndex, targetList.length);
      }
    }

    try {
      await dataSource.moveAssignment({
        assignmentId: activeId,
        milestoneDay: toDay,
        position: targetPosition,
      });
      await dataSource.invalidate();
    } catch (err) {
      handleApiError(err, "Não foi possível mover");
    }
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <main className="flex-1 min-w-0 p-8 space-y-8 overflow-y-auto">
        {!hideWelcomeHeader && (
          <header data-testid="canvas-welcome-header">
            <h1 className="text-3xl font-semibold tracking-tight">
              Boas-vindas, {"{primeiro_nome}"}!
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Prévia do header do portal. O nome do colaborador é preenchido automaticamente para
              cada novo onboarding. As mensagens podem ser personalizadas por marco abaixo.
            </p>
          </header>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveDragId(null);
            setOverDay(null);
          }}
        >
          <div className="space-y-6">
            {MILESTONE_DAYS.map((day) => {
              const dayAssignments = assignmentsByDay.get(day) ?? [];
              const isCollapsed = collapsedDays.has(day) && activeDragId === null;
              return (
                <section
                  key={day}
                  data-testid={`canvas-milestone-D${day}`}
                  className="rounded-lg border bg-card"
                >
                  <button
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-secondary/50 transition-colors ${
                      isCollapsed ? "" : "border-b"
                    }`}
                    data-testid={`canvas-milestone-toggle-D${day}`}
                    aria-expanded={!isCollapsed}
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                      <h2 className="text-sm font-semibold uppercase tracking-wide">Dia {day}</h2>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {dayAssignments.length === 0
                        ? "0 itens"
                        : dayAssignments.length === 1
                          ? "1 item"
                          : `${dayAssignments.length} itens`}
                    </span>
                  </button>
                  {!isCollapsed && (
                    <>
                      {renderWelcomeForDay && (
                        <div className="px-4 pt-3" data-testid={`canvas-welcome-slot-D${day}`}>
                          {renderWelcomeForDay(day)}
                        </div>
                      )}
                      <SortableContext
                        items={dayAssignments.map((a) => a.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <DayDropZone
                          day={day}
                          hasItems={dayAssignments.length > 0}
                          isDragging={activeDragId !== null}
                          isTarget={overDay === day}
                        >
                          {dayAssignments.length === 0 ? (
                            <p className="px-4 py-6 text-sm text-muted-foreground">
                              Nenhum recurso alocado ainda.
                            </p>
                          ) : (
                            <ul className="divide-y" data-testid={`canvas-milestone-list-D${day}`}>
                              {dayAssignments.map((a) => (
                                <AssignmentRow
                                  key={a.id}
                                  assignmentId={a.id}
                                  resource={a.resource}
                                  isOwner={isOwner}
                                  busy={dataSource.isMutationPending}
                                  onPreview={goToResourcePreview}
                                  onEdit={(r) => setEditingResourceId(r.id)}
                                  onRemove={handleRemove}
                                />
                              ))}
                            </ul>
                          )}
                        </DayDropZone>
                      </SortableContext>
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => handleAddResourceToDay(day)}
                          className="flex w-full items-center gap-1.5 border-t px-4 py-2.5 text-xs text-muted-foreground hover:bg-secondary transition-colors"
                          data-testid={`canvas-add-resource-D${day}`}
                        >
                          + Adicionar...
                        </button>
                      )}
                    </>
                  )}
                </section>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeDragId
              ? (() => {
                  const a = dataSource.assignments.find((x) => x.id === activeDragId);
                  if (!a) return null;
                  return (
                    <div
                      className="rounded-md border bg-card shadow-lg px-4 py-3 w-[480px] cursor-grabbing"
                      data-testid="drag-overlay"
                    >
                      <p className="text-sm font-medium">{a.resource.title}</p>
                      {a.resource.renderType && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {labelForRenderType(a.resource.renderType)}
                        </p>
                      )}
                    </div>
                  );
                })()
              : null}
          </DragOverlay>
        </DndContext>
      </main>

      <AddResourceModal
        open={pickerDay !== null}
        onOpenChange={(next) => {
          if (!next) {
            setPickerDay(null);
            dataSource.setPickerEnabled(false);
          }
        }}
        milestoneDay={pickerDay ?? (MILESTONE_DAYS[0] as MilestoneDay)}
        resources={dataSource.resources}
        assignments={dataSource.assignments}
        onPick={async (resource) => {
          await handlePickResource(resource);
        }}
        onResourceCreated={() => {
          void dataSource.invalidate();
        }}
      />

      <EditResourceDialog
        open={editingResourceId !== null}
        onOpenChange={(next) => {
          if (!next) setEditingResourceId(null);
        }}
        resourceId={editingResourceId}
        onSaved={() => {
          void dataSource.invalidate();
        }}
      />
    </div>
  );
}

function DayDropZone({
  day,
  hasItems,
  isDragging,
  isTarget,
  children,
}: {
  day: MilestoneDay;
  hasItems: boolean;
  isDragging: boolean;
  isTarget: boolean;
  children: React.ReactNode;
}) {
  // Only register the day container as a drop target when it has no items.
  // Otherwise the container (a large rect that includes welcome message + list)
  // wins collision detection against individual rows, making it impossible
  // to drop between rows. With this disabled, closestCenter picks the nearest
  // sortable row instead.
  const { setNodeRef } = useDroppable({ id: `day-${day}`, disabled: hasItems });
  const outline = isTarget
    ? "outline outline-2 -outline-offset-2 outline-primary bg-primary/10"
    : isDragging
      ? "outline outline-2 -outline-offset-2 outline-dashed outline-muted-foreground/40"
      : "";
  return (
    <div
      ref={setNodeRef}
      className={`relative rounded-md transition-colors ${outline}`}
      data-testid={`canvas-milestone-droppable-D${day}`}
    >
      {children}
      {isDragging && !hasItems && (
        <div
          className={`pointer-events-none absolute inset-0 m-3 rounded-md flex items-center justify-center text-xs font-medium ${
            isTarget ? "text-primary" : "text-muted-foreground"
          }`}
        >
          Soltar no Dia {day}
        </div>
      )}
    </div>
  );
}
